import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    ShoppingCart, Tag, Package,
    Storefront, ArrowsLeftRight, Crown, Heart, Receipt, Fire
} from '@phosphor-icons/react';
import { ShopItemCard } from '../components/shop/ShopItemCard';
import { ShopSearchBar } from '../components/shop/ShopSearchBar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const SUBCATEGORIES = {
    vehicles: ['electric_dirtbike', 'emtb', 'skateboard', 'euc'],
    parts: ['motors', 'batteries', 'spare_parts'],
    gear: ['helmets', 'jerseys', 'pants', 'gloves', 'protection'],
    merch: ['merch_kilovoltz', 'merch_rmr'],
    streetwear: ['hoodies', 'bottoms', 'tees', 'jackets', 'accessories'],
};

export default function Shop() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('all');
    const [search, setSearch] = useState('');
    const [tier, setTier] = useState('free');
    const [searchFocused, setSearchFocused] = useState(false);
    const [verifiedSellers, setVerifiedSellers] = useState([]);
    const [wishlistIds, setWishlistIds] = useState(new Set());
    const [drops, setDrops] = useState([]);
    const [countdown, setCountdown] = useState('');

    const [recentSearches, setRecentSearches] = useState(() => {
        try { return JSON.parse(localStorage.getItem('btr_recent_searches') || '[]'); }
        catch { return []; }
    });

    const commitSearch = (term) => {
        if (!term.trim()) return;
        const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 6);
        setRecentSearches(updated);
        localStorage.setItem('btr_recent_searches', JSON.stringify(updated));
        setSearch(term);
        setSearchFocused(false);
    };

    useEffect(() => {
        if (!drops.length) return;
        const tick = () => {
            const target = new Date(drops[0].drops_at).getTime();
            const now = Date.now();
            const diff = target - now;
            if (diff <= 0) { setCountdown('LIVE NOW'); return; }
            const d = Math.floor(diff / 86400000);
            const h = Math.floor((diff % 86400000) / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setCountdown(`${d}d ${h}h ${m}m ${s}s`);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [drops]);

    useEffect(() => {
        fetchData();
        fetch(`${API}/subscription/me`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.tier) setTier(d.tier); })
            .catch(e => console.warn('Sub:', e.message));
        fetch(`${API}/connect/verified-sellers`)
            .then(r => r.ok ? r.json() : [])
            .then(setVerifiedSellers)
            .catch(() => {});
        fetch(`${API}/wishlist/ids`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : [])
            .then(ids => setWishlistIds(new Set(ids)))
            .catch(() => {});
        fetch(`${API}/drops/upcoming`)
            .then(r => r.ok ? r.json() : [])
            .then(setDrops)
            .catch(() => {});
    }, []);

    const fetchData = async () => {
        try {
            const [itemsRes, listingsRes] = await Promise.all([
                fetch(`${API}/marketplace/items`, { credentials: 'include' }),
                fetch(`${API}/listings`, { credentials: 'include' })
            ]);
            if (itemsRes.ok) setItems(await itemsRes.json());
            if (listingsRes.ok) setListings(await listingsRes.json());
        } catch (error) {
            console.error('Shop fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const searchLower = search.toLowerCase().trim();

    const filteredItems = (category === 'all'
        ? items
        : items.filter(item => {
            if (SUBCATEGORIES[category]) {
                return item.category === category || SUBCATEGORIES[category].includes(item.subcategory);
            }
            return item.category === category;
        })
    ).filter(item =>
        !searchLower ||
        (item.name || '').toLowerCase().includes(searchLower) ||
        (item.description || '').toLowerCase().includes(searchLower) ||
        (item.brand || '').toLowerCase().includes(searchLower)
    );

    const filteredListings = (category === 'all'
        ? listings
        : listings.filter(listing => listing.category === category)
    ).filter(listing =>
        !searchLower ||
        (listing.name || listing.title || '').toLowerCase().includes(searchLower) ||
        (listing.description || '').toLowerCase().includes(searchLower)
    );

    const canBuy = tier === 'standard' || tier === 'vip';
    const canSellTrade = tier === 'vip';

    const handleItemClick = (item, isListing) => {
        if (tier === 'free') {
            toast.error('Upgrade to Standard or VIP to purchase items', {
                action: { label: 'Upgrade', onClick: () => navigate('/subscription') },
            });
            return;
        }
        navigate(isListing ? `/listings/${item.listing_id}` : `/shop/item/${item.item_id}`);
    };

    const toggleWishlist = async (e, itemId) => {
        e.stopPropagation();
        try {
            const res = await fetch(`${API}/wishlist/${itemId}`, {
                method: 'POST', credentials: 'include', headers: getH(),
            });
            if (res.ok) {
                const data = await res.json();
                setWishlistIds(prev => {
                    const next = new Set(prev);
                    data.action === 'added' ? next.add(itemId) : next.delete(itemId);
                    return next;
                });
                toast.success(data.action === 'added' ? 'Added to wishlist' : 'Removed');
            }
        } catch { toast.error('Failed'); }
    };

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="label-text text-primary text-glow">MARKETPLACE</span>
                            <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">Shop</h1>
                            <p className="text-muted-foreground mt-1 text-sm">Spend your Boltz or SOL on bikes, gear, and more</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="ghost" className="text-xs" onClick={() => navigate('/orders')} data-testid="orders-link">
                                <Receipt size={14} className="mr-1" />Orders
                            </Button>
                            <Button size="sm" variant="ghost" className="text-xs" onClick={() => navigate('/wishlist')} data-testid="wishlist-link">
                                <Heart size={14} weight={wishlistIds.size > 0 ? 'fill' : 'regular'}
                                    className={wishlistIds.size > 0 ? 'text-plasma-red mr-1' : 'mr-1'} />
                                {wishlistIds.size > 0 && <span className="text-plasma-red">{wishlistIds.size}</span>}
                            </Button>
                            <Badge className={`text-[10px] ${
                                tier === 'vip' ? 'bg-boltz-gold/20 text-boltz-gold border-boltz-gold/30' :
                                tier === 'standard' ? 'bg-primary/20 text-primary border-primary/30' :
                                'bg-white/10 text-muted-foreground border-white/10'
                            }`} data-testid="tier-badge">
                                {tier === 'vip' ? <Crown size={10} className="mr-1" /> : null}
                                {tier.toUpperCase()}
                            </Badge>
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/subscription')} data-testid="upgrade-btn">
                                {tier === 'vip' ? 'Plan' : 'Upgrade'}
                            </Button>
                        </div>
                    </div>

                    {canSellTrade && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3">
                            <Button onClick={() => navigate('/listings/create')} className="bg-boltz-gold text-black font-display text-xs hover:bg-boltz-gold/80" data-testid="my-storefront-btn">
                                <Storefront size={16} className="mr-1.5" /> My Storefront
                            </Button>
                        </motion.div>
                    )}
                </motion.div>

                {/* Drop Countdown Banner */}
                {drops.length > 0 && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                        <Card className="glass-card border-hot-pink/30 bg-hot-pink/5 mb-4 overflow-hidden cursor-pointer"
                            onClick={() => navigate('/lookbook')} data-testid="drop-countdown-banner">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-hot-pink/10 flex items-center justify-center animate-pulse">
                                            <Fire size={22} className="text-hot-pink" weight="fill" />
                                        </div>
                                        <div>
                                            <p className="font-display font-bold text-sm text-foreground">{drops[0].title}</p>
                                            <p className="text-xs text-muted-foreground">{drops[0].description?.slice(0, 60)}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] text-hot-pink font-display tracking-wider">DROPS IN</p>
                                        <p className="font-mono font-black text-lg text-hot-pink" data-testid="drop-countdown">{countdown}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Search + Category Filter */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6">
                    <ShopSearchBar
                        search={search} setSearch={setSearch}
                        searchFocused={searchFocused} setSearchFocused={setSearchFocused}
                        recentSearches={recentSearches} commitSearch={commitSearch}
                        category={category} setCategory={setCategory}
                    />
                </motion.div>

                <Tabs defaultValue="shop" className="w-full">
                    <TabsList className={`grid w-full mb-6 ${canSellTrade ? 'grid-cols-3' : 'grid-cols-2'}`}>
                        <TabsTrigger value="shop" data-testid="tab-shop"><ShoppingCart size={16} className="mr-2" />Official Shop</TabsTrigger>
                        {canSellTrade ? (
                            <>
                                <TabsTrigger value="p2p" data-testid="tab-p2p"><ArrowsLeftRight size={16} className="mr-2" />Buy / Sell / Trade</TabsTrigger>
                                <TabsTrigger value="storefront" data-testid="tab-storefront"><Storefront size={16} className="mr-2" />Storefronts</TabsTrigger>
                            </>
                        ) : (
                            <TabsTrigger value="p2p" data-testid="tab-p2p"><Tag size={16} className="mr-2" />P2P Listings</TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="shop">
                        {loading ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {[1, 2, 3, 4].map(i => (
                                    <Card key={i} className="glass-card overflow-hidden"><Skeleton className="aspect-square" /><CardContent className="p-3"><Skeleton className="h-4 w-3/4 mb-2" /><Skeleton className="h-4 w-1/2" /></CardContent></Card>
                                ))}
                            </div>
                        ) : filteredItems.length === 0 ? (
                            <Card className="glass-card"><CardContent className="p-8 text-center"><ShoppingCart size={48} className="text-muted-foreground mx-auto mb-4" /><p className="font-medium">No items in this category</p></CardContent></Card>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {filteredItems.map(item => (
                                    <ShopItemCard key={item.item_id} item={item} tier={tier}
                                        verifiedSellers={verifiedSellers} wishlistIds={wishlistIds}
                                        onItemClick={handleItemClick} onToggleWishlist={toggleWishlist} />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="p2p">
                        {canSellTrade && (
                            <div className="mb-4 flex gap-2 justify-end">
                                <Button onClick={() => navigate('/listings/create')} className="btn-primary text-xs" data-testid="create-listing-btn">
                                    Sell Item
                                </Button>
                                <Button variant="outline" className="text-xs border-electric-blue/30 text-electric-blue" data-testid="trade-btn">
                                    <ArrowsLeftRight size={14} className="mr-1" /> Trade
                                </Button>
                            </div>
                        )}
                        {!canSellTrade && tier === 'standard' && (
                            <div className="mb-4 p-3 rounded-xl bg-boltz-gold/5 border border-boltz-gold/20 text-center">
                                <p className="text-xs text-boltz-gold">
                                    <Crown size={12} className="inline mr-1" />Upgrade to VIP to sell and trade P2P
                                </p>
                            </div>
                        )}
                        {loading ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {[1,2,3,4].map(i => <Card key={i} className="glass-card overflow-hidden"><Skeleton className="aspect-square" /><CardContent className="p-3"><Skeleton className="h-4 w-3/4 mb-2" /><Skeleton className="h-4 w-1/2" /></CardContent></Card>)}
                            </div>
                        ) : filteredListings.length === 0 ? (
                            <Card className="glass-card"><CardContent className="p-8 text-center"><Tag size={48} className="text-muted-foreground mx-auto mb-4" /><p className="font-medium">No listings yet</p></CardContent></Card>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {filteredListings.map(listing => (
                                    <ShopItemCard key={listing.listing_id} item={listing} isListing tier={tier}
                                        verifiedSellers={verifiedSellers} wishlistIds={wishlistIds}
                                        onItemClick={handleItemClick} onToggleWishlist={toggleWishlist} />
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {canSellTrade && (
                        <TabsContent value="storefront">
                            <Card className="glass-card">
                                <CardContent className="p-8 text-center">
                                    <Storefront size={48} className="text-boltz-gold mx-auto mb-4" />
                                    <h3 className="font-display text-lg text-boltz-gold mb-2">VIP Storefronts</h3>
                                    <p className="text-sm text-muted-foreground mb-4">Create your own storefront and sell directly to the Boltorium community</p>
                                    <Button className="bg-boltz-gold text-black font-display" onClick={() => navigate('/listings/create')}>
                                        <Storefront size={16} className="mr-2" /> Open My Storefront
                                    </Button>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                </Tabs>
            </div>
        </Layout>
    );
}
